import { Directive, OnInit, Signal, inject, input, signal } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { Observable, defer, firstValueFrom, of, switchMap } from "rxjs";
import { Constructor } from "type-fest";

import { OrganizationService } from "@vowkey/common/admin-console/abstractions/organization/organization.service.abstraction";
import { PolicyApiServiceAbstraction } from "@vowkey/common/admin-console/abstractions/policy/policy-api.service.abstraction";
import { PolicyType } from "@vowkey/common/admin-console/enums";
import { Organization } from "@vowkey/common/admin-console/models/domain/organization";
import { SavePolicyRequest } from "@vowkey/common/admin-console/models/request/save-policy.request";
import { PolicyStatusResponse } from "@vowkey/common/admin-console/models/response/policy-status.response";
import { PolicyResponse } from "@vowkey/common/admin-console/models/response/policy.response";
import { AccountService } from "@vowkey/common/auth/abstractions/account.service";
import { getUserId } from "@vowkey/common/auth/services/account.service";
import { assertNonNullish } from "@vowkey/common/auth/utils";
import { ConfigService } from "@vowkey/common/platform/abstractions/config/config.service";
import { getById } from "@vowkey/common/platform/misc";
import { OrganizationId } from "@vowkey/common/types/guid";
import { OrgKey } from "@vowkey/common/types/key";
import { DialogConfig, DialogRef, DialogService } from "@vowkey/components";
import { KeyService } from "@vowkey/key-management";

import { PolicyCategory } from "./pipes/policy-category";
import type { PolicyStep, PolicyStepResult } from "./policy-edit-dialogs/models";
import type { PolicyEditDialogData, PolicyEditDialogResult } from "./policy-edit-drawer.component";

/**
 * Interface for policy dialog components.
 * Any component that implements this interface can be used as a custom policy edit dialog.
 */
export interface PolicyDialogComponent {
  openDrawer: (
    dialogService: DialogService,
    config: DialogConfig<PolicyEditDialogData>,
  ) => Promise<DialogRef<PolicyEditDialogResult> | undefined>;
}

/**
 * A metadata class that defines how a policy is displayed in the Admin Console Policies page for editing.
 * Add this to the `ossPolicyRegister` or `bitPolicyRegister` file to register it in the application.
 */
export abstract class BasePolicyEditDefinition {
  /**
   * i18n string for the policy name.
   */
  abstract name: string;
  /**
   * Optional i18n key for the VFO1 "vault terminology" variant of {@link name}, shown in the
   * policies list when {@link FeatureFlag.VFO1Foundation} is enabled. Falls back to {@link name}
   * when not set.
   */
  nameVfo1?: string;
  /**
   * Optional i18n key for the VFO1 "vault terminology" variant of {@link name} used in the
   * drawer/dialog title. Falls back to {@link nameVfo1} when not set.
   */
  drawerNameVfo1?: string;
  /**
   * i18n string for the policy description.
   * This is shown in the list of policies and in the policy edit drawer.
   */
  abstract description: string;
  /**
   * Optional i18n key for the VFO1 "vault terminology" variant of {@link description}, shown in
   * the policies list when {@link FeatureFlag.VFO1Foundation} is enabled. Falls back to
   * {@link description} when not set.
   */
  descriptionVfo1?: string;
  /**
   * Optional i18n key for the VFO1 "vault terminology" variant of {@link description} used in the
   * drawer/dialog body. Falls back to {@link descriptionVfo1} when not set.
   */
  drawerDescriptionVfo1?: string;

  /**
   * The PolicyType enum that this policy represents.
   */
  abstract type: PolicyType;
  /**
   * The category this policy belongs to. Used to group policies on the Policies page.
   */
  abstract category: PolicyCategory;
  /**
   * The sort order of this policy within its category on the Policies page.
   * Lower numbers appear first. Values only need to be consistent relative to
   * other policies in the same category.
   */
  abstract priority: number;
  /**
   * The component used to edit this policy. See {@link BasePolicyEditComponent}.
   */
  abstract component: Constructor<BasePolicyEditComponent>;

  /**
   * The dialog component that will be opened when editing this policy.
   * This allows customizing the look and feel of each policy's dialog contents.
   */
  editDialogComponent?: PolicyDialogComponent;

  /**
   * If true, the {@link description} will be reused in the policy edit modal. Set this to false if you
   * have more complex requirements that you will implement in your template instead.
   **/
  showDescription: boolean = true;

  /**
   * Optional i18n key for a warning callout rendered by {@link PolicyEditDrawerComponent}
   * above the policy form.
   */
  warningKey?: string;
  /**
   * Optional i18n key for the VFO1 "vault terminology" variant of {@link warningKey}. Falls back
   * to {@link warningKey} when not set.
   */
  warningKeyVfo1?: string;
  /**
   * If true, hides the {@link warningKey} callout entirely when the
   * {@link FeatureFlag.VFO1Foundation} flag is enabled. Legacy behavior (flag off) is unaffected.
   */
  hideWarningVfo1?: boolean;

  /**
   * Optional i18n key for a prerequisite info callout rendered by {@link PolicyEditDrawerComponent}
   * above the policy form.
   */
  prerequisiteKey?: string;
  /**
   * Optional i18n key for the VFO1 "vault terminology" variant of {@link prerequisiteKey}. Falls
   * back to {@link prerequisiteKey} when not set.
   */
  prerequisiteKeyVfo1?: string;
  /** URL for an optional "learn more" link inside the prerequisite callout. */
  prerequisiteLinkHref?: string;
  /** i18n key for the text of {@link prerequisiteLinkHref}. */
  prerequisiteLinkTextKey?: string;

  /**
   * A method that determines whether to display this policy in the Admin Console Policies page.
   * The default implementation will always display the policy.
   * This can be used to hide the policy based on the organization's plan features or a feature flag value.
   * Note: this only hides the policy for editing in Admin Console, it does not affect its enforcement
   * if it has already been turned on. Enforcement should be feature flagged separately.
   */
  display$(organization: Organization, configService: ConfigService): Observable<boolean> {
    return of(true);
  }

  /**
   * Logic for displaying the policy status in the Admin Console.
   * If this returns true, the policy is shown as enabled. If false, it is shown as disabled.
   * This uses the `policy.enabled` value by default, which is appropriate for most cases.
   * You may wish to override this if the UI does not perfectly match the data model, e.g.
   * you wish to determine policy status based on a `policy.data` value.

   * Note: this only affects policy editing in Admin Console, it does not affect its enforcement.
   */
  enabled(policy: PolicyResponse | PolicyStatusResponse): boolean {
    return policy.enabled;
  }
}

/**
 * Returns the [legacy, VFO1] i18n key pair for a policy's title in the policies list.
 * Used by {@link PoliciesComponent}.
 */
export function policyTitleKeys(policy: BasePolicyEditDefinition): [string, string] {
  return [policy.name, policy.nameVfo1 ?? policy.name];
}

/**
 * Returns the [legacy, VFO1] i18n key pair for a policy's title in the drawer/dialog.
 * Falls back to {@link policyTitleKeys} when no drawer-specific VFO1 key is set.
 * Used by {@link PolicyEditDrawerComponent} and {@link MultiStepPolicyEditDialogComponent}.
 */
export function policyDrawerTitleKeys(policy: BasePolicyEditDefinition): [string, string] {
  return [policy.name, policy.drawerNameVfo1 ?? policy.nameVfo1 ?? policy.name];
}

/**
 * Returns the [legacy, VFO1] i18n key pair for a policy's description in the policies list.
 * Used by {@link PoliciesComponent}.
 */
export function policyDescriptionKeys(policy: BasePolicyEditDefinition): [string, string] {
  return [policy.description, policy.descriptionVfo1 ?? policy.description];
}

/**
 * Returns the [legacy, VFO1] i18n key pair for a policy's description in the drawer/dialog body.
 * Falls back to {@link policyDescriptionKeys} when no drawer-specific VFO1 key is set.
 * Used by {@link PolicyEditDrawerComponent} and {@link MultiStepPolicyEditDialogComponent}.
 */
export function policyDrawerDescriptionKeys(policy: BasePolicyEditDefinition): [string, string] {
  return [
    policy.description,
    policy.drawerDescriptionVfo1 ?? policy.descriptionVfo1 ?? policy.description,
  ];
}

/**
 * A component used to edit the policy settings in Admin Console. It is rendered inside the PolicyEditDrawerComponent.
 * This should contain the form controls used to edit the policy (including the Enabled checkbox) and any additional
 * warnings or callouts.
 * See existing implementations as a guide.
 */
@Directive()
export abstract class BasePolicyEditComponent implements OnInit {
  protected readonly accountService = inject(AccountService);
  protected readonly organizationServcie = inject(OrganizationService);
  protected readonly keyService = inject(KeyService);
  protected readonly policyApiService = inject(PolicyApiServiceAbstraction);

  readonly policyResponse = input<PolicyStatusResponse | undefined>(undefined);
  readonly policy = input<BasePolicyEditDefinition | undefined>(undefined);
  readonly currentStep = input<Signal<number>>(signal(0));
  readonly organizationId = input<string | undefined>(undefined);
  readonly organization$ = defer(() =>
    this.accountService.activeAccount$.pipe(
      getUserId,
      switchMap((userId) => this.organizationServcie.organizations$(userId)),
      getById(this.organizationId() ?? this.policyResponse()?.organizationId),
    ),
  );

  /**
   * Whether the policy is enabled.
   */
  enabled = new FormControl(false);

  /**
   * An optional FormGroup for additional policy configuration. Required for more complex policies only.
   */
  data: FormGroup | undefined;

  /**
   * Optional multi-step configuration for policies that require multiple steps to complete.
   * Defaults to a single step that saves the policy.
   */
  policySteps: PolicyStep[] = [{ sideEffect: () => this.savePolicy() }];

  ngOnInit(): void {
    this.enabled.setValue(this.policyResponse()?.enabled ?? false);

    if (this.policyResponse()?.data != null) {
      this.loadData();
    }
  }

  async buildRequest(orgKey?: OrgKey): Promise<SavePolicyRequest> {
    if (!this.policy()) {
      throw new Error("Policy was not found");
    }

    return {
      policy: {
        enabled: this.enabled.value ?? false,
        data: this.buildRequestData(),
      },
      metadata: null,
    };
  }

  /**
   * Saves the policy. Subclasses that require additional steps or side effects
   * (e.g. enabling a prerequisite policy) should override this method.
   */
  protected async savePolicy(): Promise<PolicyStepResult | void> {
    if (!this.policy()) {
      throw new Error("Policy was not found");
    }

    const orgKeys = await firstValueFrom(
      this.accountService.activeAccount$.pipe(
        getUserId,
        switchMap((userId) => this.keyService.orgKeys$(userId)),
      ),
    );

    assertNonNullish(orgKeys, "Org keys not provided");

    const orgKey = orgKeys[this.organizationId() as OrganizationId];

    assertNonNullish(orgKey, "No encryption key for this organization.");

    const request = await this.buildRequest(orgKey);

    await this.policyApiService.putPolicy(
      this.organizationId() ?? "",
      this.policy()!.type,
      request,
    );
  }

  protected loadData() {
    this.data?.patchValue(this.policyResponse()?.data ?? {});
  }

  /**
   * Transforms the {@link data} FormGroup to the policy data model for saving.
   */
  protected buildRequestData() {
    if (this.data != null) {
      return this.data.getRawValue();
    }

    return null;
  }
}
